import { Application } from '../../vendor/pixi.min.mjs';
import { InputSystem } from '../systems/InputSystem.js';
import { SkinResolver } from './SkinResolver.js';
import { loadTranslations, setLang } from '../config/i18n.js';

const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;
const TARGET_FPS = 60;

/**
 * Root game controller.
 * Canvas is locked at 1920x1080. The canvas is CSS-scaled to fit the window once at boot.
 */
export class Game {
    /** @type {Application} */
    app = null;

    /** @type {import('./SceneManager.js').SceneManager|null} */
    sceneManager = null;

    /** @type {InputSystem} */
    input = null;

    /** @type {SkinResolver} */
    skin = null;

    /** @type {object|null} Merged config injected from main.js */
    config = null;

    /** @type {Function[]} update subscribers */
    _updateCallbacks = [];

    async init() {
        const bg = this.config?.canvas?.backgroundColor ?? 0x0b0f17;
        const aa = this.config?.canvas?.antialias ?? true;

        this.app = new Application();

        await this.app.init({
            background: bg,
            antialias: aa,
            resolution: 1,
            autoDensity: false,
            width: DESIGN_WIDTH,
            height: DESIGN_HEIGHT,
        });

        document.body.appendChild(this.app.canvas);
        this._applyFit();

        this.input = new InputSystem();
        this.input.init(this.app.canvas);

        loadTranslations();

        this.skin = new SkinResolver(this.config);
        // SkinResolver's skin/tileset stores now live in IndexedDB
        // (so MP3 / PNG imports actually survive a reload past the
        // ~5 MB localStorage quota). The IDB read is async — wait
        // for it before the rest of boot reads from the resolver.
        await this.skin.ready();

        // Honour the deployment-wide language restriction. Two layers:
        //   1. Build-time `__BUILD_LANG_MODE__` — defined by esbuild
        //      from `data/shared.config.js → availableLanguages.default`.
        //      'hu' / 'en' here means the bundle is hard-locked to that
        //      locale and the toggle UI was DCE'd out entirely.
        //   2. Runtime setting `availableLanguages` — the operator's
        //      in-app override (when the build is 'both'). Same
        //      semantics: 'hu' / 'en' pins the locale, 'both' lets
        //      the user toggle.
        // Build-time wins so a single-locale bundle stays single-locale
        // even if a returning visitor's localStorage says otherwise.
        const buildLangMode = typeof globalThis.__BUILD_LANG_MODE__ !== 'undefined'
            ? globalThis.__BUILD_LANG_MODE__
            : 'both';
        if (buildLangMode === 'hu' || buildLangMode === 'en') {
            this.skin.setLang(buildLangMode);
        } else {
            const langRestriction = this.skin.getSetting?.(
                'shared.settings',
                'availableLanguages',
                'both',
            );
            if (langRestriction === 'hu' || langRestriction === 'en') {
                this.skin.setLang(langRestriction);
            }
        }
        setLang(this.skin.getLang());

        this._startLoop();

        return this;
    }

    /** Backwards-compat alias: legacy code reads `game.theme`. */
    get theme() { return this.skin; }

    /** Register a callback that receives (delta) every frame */
    onUpdate(callback) {
        this._updateCallbacks.push(callback);
    }

    /** Remove a previously registered update callback */
    offUpdate(callback) {
        const idx = this._updateCallbacks.indexOf(callback);
        if (idx !== -1) this._updateCallbacks.splice(idx, 1);
    }

    /** Locked design width (1920) */
    get width() {
        return DESIGN_WIDTH;
    }

    /** Locked design height (1080) */
    get height() {
        return DESIGN_HEIGHT;
    }

    /** The root display-object container */
    get stage() {
        return this.app.stage;
    }

    // ── private ────────────────────────────────────────

    _applyFit() {
        const canvas = this.app.canvas;
        const fit = Math.min(
            window.innerWidth / DESIGN_WIDTH,
            window.innerHeight / DESIGN_HEIGHT
        );
        canvas.style.position = 'absolute';
        canvas.style.left = '50%';
        canvas.style.top = '50%';
        canvas.style.transformOrigin = 'center center';
        canvas.style.transform = `translate(-50%, -50%) scale(${fit})`;
    }

    _startLoop() {
        this.app.ticker.maxFPS = TARGET_FPS;
        this.app.ticker.add((ticker) => {
            const delta = ticker.deltaTime;
            for (let i = 0; i < this._updateCallbacks.length; i++) {
                this._updateCallbacks[i](delta);
            }
        });
    }
}
